import { IconInfoCircle } from '@tabler/icons-react'
import { Dayjs } from 'dayjs'

import { PoliclinicStatuses, PredictedWaitingListBars, ShowOnlyAvailableWaitingListToggle, WaitingListCallout } from '~/app/pages/SurgeonScheduler/shared'
import { usePractitionerPredictedWaitingList } from '~/app/pages/SurgeonScheduler/TablesLayout/Practitioners/usePractitionerPredictedWaitingList'
import {
    AstraAccordion,
    AstraCallout,
    AstraHeading,
    AstraIcon,
    AstraParagraph,
    AstraSkeletonLoader,
    DATE_TEMPLATES,
    format,
    getOnlyDate,
    getToday,
} from '~/astra'
import { StatusCode } from '~/store/slices/filterSlice'
import { Practitioner, usePractitionerScheduleStatuses } from '~/storm'

import { PopoverContentWrapper } from './PopoverContentWrapper'

type Props = {
    practitioner: Practitioner
    date: Dayjs
}

const totalWaitingListStyle = 'bg-[#9DDDE7] rounded p-[2px]'
const predictedDecreaseStyle = 'bg-[#C1D0FF] rounded p-[2px]'
const predictedIncreaseStyle = 'bg-[#FFDFB5] rounded p-[2px]'
const highlightedTextStyle = 'font-semibold text-default'

const Header = ({ date }: { date: Dayjs }) => (
    <div className="flex justify-between pb-3.5">
        <AstraHeading as="h3" className="px-3 py-2 font-medium text-default">
            {`Predikert for ${format(date, 'MMMM YYYY')}`}
        </AstraHeading>
        <ShowOnlyAvailableWaitingListToggle />
    </div>
)

export const PredictedWaitingListPopoverContent = ({ practitioner, date }: Props) => {
    const predictedWaitingList = usePractitionerPredictedWaitingList(practitioner)

    const today = getToday()
    const currentMonth = format(date, 'MMMM')
    const statusesByDateAndCode = usePractitionerScheduleStatuses({
        where: {
            practitioner_id: practitioner.id,
            scheduled_date: { gte: getOnlyDate(date.startOf('month')), lte: getOnlyDate(date.endOf('month')) },
        },
        keyBy: ['scheduled_date', 'status_code'],
    })

    const dedupedStatusCodes = [...statusesByDateAndCode.values()].map(s => s.status_code)

    const {
        isLoading,
        isError,
        currentUnscheduledWaitingList,
        futureBookedSurgeries,
        availableCapacityUntilThisMonth,
        increaseUntilThisMonth,
        waitingListStartOfMonth,
        availableCapacityThisMonth,
        increaseThisMonth,
        waitingListEndOfMonth,
        alreadyBookedSurgeriesThisMonth,
        totalPredictedSurgeriesThisMonth,
        isPredictedBelowSafeLevels,
    } = predictedWaitingList

    const getCounts = (statuses: StatusCode[]) => dedupedStatusCodes.filter(code => statuses.includes(code)).length

    if (isLoading) {
        return (
            <PopoverContentWrapper>
                <Header date={date} />
                <div className="flex h-full w-96 flex-col gap-3 self-center overflow-hidden">
                    <AstraHeading as="h3" className="px-3 font-medium text-default">
                        Laster...
                    </AstraHeading>
                    <div className="flex flex-col rounded-md border bg-surface-base p-3.5">
                        <div className="h-10 w-full py-2">
                            <AstraSkeletonLoader />
                        </div>
                        <hr className="-mx-2 pb-6" />
                        <PredictedWaitingListBars
                            isLoading={true}
                            initialWaitingList={0}
                            totalPredictedSurgeries={0}
                            bookedSurgeries={0}
                            predictedIncrease={0}
                            finalWaitingList={0}
                        />
                    </div>
                </div>
            </PopoverContentWrapper>
        )
    }

    if (isError) {
        return (
            <PopoverContentWrapper>
                <Header date={date} />
                <WaitingListCallout type="error" />
            </PopoverContentWrapper>
        )
    }

    return (
        <PopoverContentWrapper>
            <Header date={date} />
            <div className="flex h-full w-full flex-col gap-4 self-center overflow-hidden">
                <div className="flex flex-col rounded-md border bg-surface-base px-4 py-3.5">
                    <div className="flex items-baseline gap-x-2 py-2">
                        <AstraHeading as="h1">{waitingListEndOfMonth}</AstraHeading>
                        <AstraParagraph variant="body-small" weight="medium" className="text-muted">
                            pasienter på venteliste ved slutten av perioden
                        </AstraParagraph>
                    </div>
                    <hr className="-mx-2 pb-6" />
                    <PredictedWaitingListBars
                        isLoading={isLoading}
                        initialWaitingList={waitingListStartOfMonth}
                        totalPredictedSurgeries={totalPredictedSurgeriesThisMonth}
                        bookedSurgeries={alreadyBookedSurgeriesThisMonth}
                        predictedIncrease={increaseThisMonth}
                        finalWaitingList={waitingListEndOfMonth}
                    />
                </div>
                {isPredictedBelowSafeLevels && (
                    <AstraCallout
                        color="caution"
                        text={`For få pasienter (${waitingListEndOfMonth}) på predikert venteliste til å fylle alle operasjonsdager.`}
                        size="medium"
                    />
                )}
                <AstraAccordion
                    itemSeparator="gap"
                    items={[
                        {
                            value: 'predictedInfo',
                            title: 'Hvordan er tallene beregnet?',
                            triggerIcon: <AstraIcon icon={IconInfoCircle} />,
                            content: (
                                <div className="flex flex-col gap-2">
                                    <AstraParagraph className="pb-2" variant="body">
                                        <span className={highlightedTextStyle}>Eksisterende venteliste</span> består i dag (
                                        {format(today, DATE_TEMPLATES.longMonth)}) av{' '}
                                        <span className={totalWaitingListStyle}>{currentUnscheduledWaitingList}</span> pasienter, og forventes å være{' '}
                                        <span className={totalWaitingListStyle}>{waitingListStartOfMonth}</span> ved starten av {currentMonth} (medregnet
                                        framtidig bestilte operasjoner). Dette følger av at det er tilgjengelig kapasitet til å operere{' '}
                                        <span className={totalWaitingListStyle}>{availableCapacityUntilThisMonth}</span> pasienter fra i dag til 1.{' '}
                                        {currentMonth} og at det er bestilt <span className={totalWaitingListStyle}>{futureBookedSurgeries}</span> operasjoner
                                        etter 1. {currentMonth}, samt at <span className={totalWaitingListStyle}>{increaseUntilThisMonth}</span> pasienter
                                        forventes å bli utredet gjennom poliklinisk aktivitet i perioden.
                                    </AstraParagraph>

                                    <AstraParagraph variant="body">
                                        <span className={highlightedTextStyle}>Planlagt aktivitet i {currentMonth}:</span>
                                    </AstraParagraph>
                                    <ul className="list-disc pl-5">
                                        <li>
                                            <AstraParagraph variant="body">
                                                I {currentMonth} er det planlagt omtrent{' '}
                                                <span className={predictedDecreaseStyle}>{totalPredictedSurgeriesThisMonth}</span> operasjoner som følge av{' '}
                                                <span className={highlightedTextStyle}>{getCounts(['SURGERY'])} O-dager</span> og{' '}
                                                <span className={highlightedTextStyle}>{getCounts(['DAY_SURGERY'])} D-dager</span> (og eventuell oppsatt
                                                kapasitetsplan). Av disse har <span className={predictedDecreaseStyle}>{alreadyBookedSurgeriesThisMonth}</span>{' '}
                                                allerede blitt bestilt og det er tilgjengelig kapasitet for omtrent{' '}
                                                <span className={predictedDecreaseStyle}>{availableCapacityThisMonth}</span> ytterligere operasjoner.
                                            </AstraParagraph>
                                        </li>
                                        <li>
                                            <AstraParagraph variant="body">
                                                Ventelisten vil bli økt med omtrent <span className={predictedIncreaseStyle}>{increaseThisMonth}</span>{' '}
                                                pasienter som følge av <span className={highlightedTextStyle}>{getCounts(PoliclinicStatuses)} Poli-dager</span>{' '}
                                                og <span className={highlightedTextStyle}>{getCounts(['EVENING_POLICLINIC'])} KvP-dager</span>.
                                            </AstraParagraph>
                                        </li>
                                    </ul>
                                </div>
                            ),
                        },
                    ]}
                />
            </div>
        </PopoverContentWrapper>
    )
}
